import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Loader2, Plus, Handshake, Wallet, PlayCircle, PoundSterling } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (n) => `£${(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LEDGER_C = { committed: 'bg-blue-100 text-blue-700', owed: 'bg-amber-100 text-amber-700', settled: 'bg-emerald-100 text-emerald-700' };

const Partners = () => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ committed: 0, owed: 0, settled: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', default_split_pct: '20', notes: '' });
  const [detail, setDetail] = useState(null);
  const [credAmt, setCredAmt] = useState('');
  const [credNote, setCredNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/commercial/partners`);
      setItems(data.items || []); setTotals(data.totals || { committed: 0, owed: 0, settled: 0 });
    } catch (e) { toast.error('Failed to load partners'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/commercial/partners`, {
        name: form.name, email: form.email || null,
        default_split_pct: parseFloat(form.default_split_pct) || 0, notes: form.notes || null,
      });
      toast.success('Partner created'); setDlg(false);
      setForm({ name: '', email: '', default_split_pct: '20', notes: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    const { data } = await axios.get(`${API}/commercial/partners/${id}`);
    setDetail(data); setCredAmt(''); setCredNote('');
  };

  const addCreditor = async () => {
    if (!credAmt) return;
    setBusy(true);
    try {
      await axios.post(`${API}/commercial/partners/${detail.id}/creditor`, { amount: parseFloat(credAmt), note: credNote || null });
      toast.success('Creditor entry added'); openDetail(detail.id); load();
    } catch (e) { toast.error('Failed'); } finally { setBusy(false); }
  };

  const runSettlement = async () => {
    if (!window.confirm('Run monthly settlement? All "owed" commission becomes "settled".')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/commercial/settlement/run`);
      toast.success(`Settled ${gbp(data.total)} across ${data.lines.length} partner(s)`);
      load(); if (detail) openDetail(detail.id);
    } catch (e) { toast.error('Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="partners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Partners & commission</h1>
          <p className="text-gray-500 text-sm mt-1">Partner records with dual ledgers. Commission earns as orders are paid and delivered.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runSettlement} disabled={busy || totals.owed <= 0} data-testid="run-settlement-btn"><PlayCircle className="w-4 h-4 mr-1" /> Run settlement</Button>
          <Button className="bg-[#00A79D] hover:bg-[#00857D]" onClick={() => setDlg(true)} data-testid="new-partner-btn"><Plus className="w-4 h-4 mr-1" /> New partner</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4" data-testid="partner-totals">
        {[['Committed', totals.committed, 'text-blue-700'], ['Owed (earned)', totals.owed, 'text-amber-700'], ['Settled', totals.settled, 'text-emerald-700']].map(([l, v, c]) => (
          <Card key={l}><CardContent className="p-4">
            <p className="text-xs text-gray-500">{l}</p>
            <p className={`text-2xl font-bold mt-1 ${c}`}>{gbp(v)}</p>
          </CardContent></Card>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
      ) : (
        <div className="space-y-2" data-testid="partners-list">
          {items.length === 0 && <div className="border border-dashed border-gray-200 rounded-lg py-16 text-center text-gray-400">No partners yet.</div>}
          {items.map((p) => (
            <button key={p.id} onClick={() => openDetail(p.id)} data-testid={`partner-row-${p.id}`}
              className="w-full text-left border border-gray-200 rounded-lg px-4 py-3 hover:border-[#00A79D]/40 transition-colors flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <Handshake className="w-5 h-5 text-[#00A79D] shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{p.ref} · {p.name}</p>
                  <p className="text-xs text-gray-500 truncate">{p.email || 'No email'} · {p.default_split_pct}% default split</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 text-xs">
                <Badge className={LEDGER_C.committed}>{gbp(p.totals.committed)} committed</Badge>
                <Badge className={LEDGER_C.owed}>{gbp(p.totals.owed)} owed</Badge>
                <Badge className={LEDGER_C.settled}>{gbp(p.totals.settled)} settled</Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* New partner dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="partner-dialog">
          <DialogHeader><DialogTitle>New partner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="partner-email" /></div>
            <div><Label>Default commission split (%)</Label><Input type="number" value={form.default_split_pct} onChange={(e) => setForm({ ...form, default_split_pct: e.target.value })} data-testid="partner-split" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="partner-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
            <Button className="bg-[#00A79D] hover:bg-[#00857D]" onClick={create} disabled={busy} data-testid="save-partner-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="partner-detail-dialog" className="max-w-lg">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Handshake className="w-5 h-5 text-[#00A79D]" /> {detail.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-2">
                {[['Committed', detail.totals.committed, 'text-blue-700'], ['Owed', detail.totals.owed, 'text-amber-700'], ['Settled', detail.totals.settled, 'text-emerald-700']].map(([l, v, c]) => (
                  <div key={l} className="border border-gray-200 rounded-lg p-2 text-center"><p className="text-[10px] text-gray-400">{l}</p><p className={`font-bold ${c}`}>{gbp(v)}</p></div>
                ))}
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1 flex items-center gap-1"><Wallet className="w-4 h-4" /> Commission ledger (due to partner)</p>
                {(detail.partner_ledger || []).length === 0 ? <p className="text-xs text-gray-400">No commission yet. Earns automatically from paid + delivered orders.</p> : (
                  <div className="space-y-1">
                    {detail.partner_ledger.map((e) => (
                      <div key={e.id} className="flex items-center justify-between border border-gray-100 rounded px-2 py-1.5" data-testid={`ledger-entry-${e.id}`}>
                        <span className="text-gray-600 text-xs">{e.order_ref}</span>
                        <div className="flex items-center gap-2"><span className="font-medium">{gbp(e.amount)}</span><Badge className={`${LEDGER_C[e.status]} text-[10px]`}>{e.status}</Badge></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1 flex items-center gap-1"><PoundSterling className="w-4 h-4" /> Creditor ledger (owed by partner to us): {gbp(detail.totals.creditor_balance)}</p>
                {(detail.creditor_ledger || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-xs text-gray-600 px-2 py-1"><span>{e.note || '-'}</span><span>{gbp(e.amount)}</span></div>
                ))}
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Amount" type="number" value={credAmt} onChange={(e) => setCredAmt(e.target.value)} className="h-8" data-testid="creditor-amount" />
                  <Input placeholder="Note" value={credNote} onChange={(e) => setCredNote(e.target.value)} className="h-8" data-testid="creditor-note" />
                  <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={addCreditor} disabled={busy || !credAmt} data-testid="add-creditor-btn">Add</Button>
                </div>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
